import styled from 'styled-components'

import React from 'react'
import { Button, CopyValue, IconTile } from '@root/components'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useBusinessContext } from '@root/context'
import {
  fetchEmailInvoiceAddress,
  IPurchaseInvoiceImportErrorPayload,
  startPurchaseInvoiceImport
} from '@root/query'
import { Trans, useTranslation } from 'react-i18next'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import { FaArrowUpFromBracket, FaCloudArrowUp, FaEnvelope } from 'react-icons/fa6'
import { useTheme } from '@hooks'
import { isGermanBusinessCountry } from './xrechnungDelivery'
import {
  getPurchaseInvoiceImportCopyKeys,
  resolveImportErrorMessage
} from './purchaseInvoiceImportCopy'

interface Props {
  onImportStarted: (payload: { jobId: string; purchaseInvoiceId: number | null }) => void
}

const PRIMARY_SOFT_ALPHA = '22'

export const PurchaseInvoiceImport: React.FC<Props> = ({ onImportStarted }) => {
  const { slug, data: business } = useBusinessContext()
  const theme = useTheme()
  const isGermanBusiness = isGermanBusinessCountry(business?.country)
  const importCopy = getPurchaseInvoiceImportCopyKeys(isGermanBusiness)
  const iconTileBackground = `${theme.colors.primary}${PRIMARY_SOFT_ALPHA}`

  const { data, isLoading } = useQuery({
    queryKey: [slug, 'email_receive_address'],
    queryFn: () => fetchEmailInvoiceAddress({ slug })
  })
  const [t] = useTranslation()

  const { mutate: startImport, isPending: isStartingImport } = useMutation({
    mutationFn: (file: File) => startPurchaseInvoiceImport({ slug }, { file, name: file.name }),
    onSuccess: response => {
      onImportStarted({
        jobId: response.job_id,
        purchaseInvoiceId: response.purchase_invoice_id ?? null
      })
    },
    onError: error => {
      const payload = (error as { data?: IPurchaseInvoiceImportErrorPayload })?.data
      toast.error(resolveImportErrorMessage(payload, isGermanBusiness, t))
    }
  })

  const isImportInProgress = isStartingImport

  const { open, getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    noDragEventsBubbling: true,
    disabled: isImportInProgress,
    accept: importCopy.accept,
    onDrop: acceptedFiles => {
      if (isImportInProgress) return
      const file = acceptedFiles[0]
      if (!file) return
      startImport(file)
    }
  })

  return (
    <StyledPage>
      <Intro>{t('invoicing.purchaseInvoiceImport.intro')}</Intro>

      <CardsGrid>
        <UploadCard className={isDragActive ? 'is-drag-active' : ''} {...getRootProps()}>
          <input {...getInputProps()} />
          <IconTile
            color={iconTileBackground}
            iconColor={theme.colors.primary}
            icon={<FaCloudArrowUp />}
          />
          <CardTitle>{t('invoicing.purchaseInvoiceImport.uploadHeader')}</CardTitle>
          <CardDescription>{t(importCopy.uploadDescription)}</CardDescription>
          <CardFooter>
            <SupportedFileTypesText>{t(importCopy.supportedFileTypes)}</SupportedFileTypesText>
            <Button
              type="button"
              icon={<FaArrowUpFromBracket />}
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                open()
              }}
              disabled={isImportInProgress}
            >
              {t('invoicing.purchaseInvoiceImport.selectFileButton')}
            </Button>
          </CardFooter>
        </UploadCard>

        <EmailCard>
          <IconTile
            color={iconTileBackground}
            iconColor={theme.colors.primary}
            icon={<FaEnvelope />}
          />
          <CardTitle>{t('invoicing.purchaseInvoiceImport.emailHeader')}</CardTitle>
          <CardDescription>
            <Trans i18nKey="invoicing.purchaseInvoiceImport.emailDescription">
              <a
                href={t('link.docs.purchaseInvoiceEmailImport')}
                rel="noreferrer"
                target="_blank"
              />
            </Trans>
          </CardDescription>
          <EmailAddressBlock>
            <CopyValue value={data?.email} isLoading={isLoading} />
          </EmailAddressBlock>
        </EmailCard>
      </CardsGrid>
    </StyledPage>
  )
}

const StyledPage = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.neutralWhite};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

const Intro = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.iconGrayDark};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg}rem;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    grid-template-columns: 1fr;
  }
`

const CardBase = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  min-width: 0;
`

const UploadCard = styled(CardBase)`
  transition: background 0.15s ease, border-color 0.15s ease;

  &.is-drag-active {
    background: ${({ theme }) => `${theme.colors.primary}${PRIMARY_SOFT_ALPHA}`};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const EmailCard = styled(CardBase)``

const CardTitle = styled.h3`
  margin: ${({ theme }) => theme.spacing.md}rem 0 ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.menuBg};
`

const CardDescription = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.lg}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  flex: 1;

  a {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
`

const EmailAddressBlock = styled.div`
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
`

const SupportedFileTypesText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`
